import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Fingerprint, AlertCircle } from 'lucide-react';
import { dataIdToolApi } from '../../lib/toolsApi';

const MODE_INFO = [
  {
    id: 'BOTH',
    name: 'Both Bytes (Profile 1 default)',
    description: 'Both the low and high byte of the 16-bit Data ID are folded into the CRC on every message. Nothing extra is transmitted.',
    idSpace: '65,536 IDs',
  },
  {
    id: 'ALT',
    name: 'Alternating (odd/even counter)',
    description: 'Only one byte is folded into the CRC per message: the low byte on even counter values, the high byte on odd counter values. Full 16-bit protection is achieved across two consecutive messages.',
    idSpace: '65,536 IDs (over 2 messages)',
  },
  {
    id: 'LOW',
    name: 'Low Byte Only',
    description: 'Only the low byte of the Data ID is folded into the CRC. Reduces the effective masquerade-protection ID space to 8 bits.',
    idSpace: '256 IDs',
  },
  {
    id: 'NIBBLE',
    name: 'Nibble (Variant 1C)',
    description: 'The low byte is folded into the CRC (with the high byte forced to zero for the CRC computation). The low nibble of the Data ID\'s high byte is additionally transmitted explicitly in the upper nibble of the header\'s counter byte.',
    idSpace: '4,096 IDs (12-bit)',
  },
];

const DataIdCalculator = () => {
  const [dataIdInput, setDataIdInput] = useState('0123');
  const [counter, setCounter] = useState(0);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const calculate = async () => {
    setError(null);
    setResult(null);
    try {
      const cleaned = dataIdInput.replace(/^0x/i, '');
      if (!/^[0-9a-fA-F]+$/.test(cleaned)) throw new Error('Data ID must be a hex value.');
      const dataId = parseInt(cleaned, 16);
      const outcome = await dataIdToolApi.breakdown(dataId, counter);
      setResult(outcome);
    } catch (e) {
      setError(e.message);
    }
  };

  return (
    <div className="grid lg:grid-cols-5 gap-6">
      <Card className="technical-card lg:col-span-2 h-fit">
        <CardHeader>
          <CardTitle className="font-chivo uppercase tracking-tight flex items-center gap-2">
            <Fingerprint className="h-5 w-5 text-autoxium-blue" />
            Data ID Calculator
          </CardTitle>
          <CardDescription>
            AUTOSAR E2E Profile 1 defines four modes for how a 16-bit Data ID is folded into
            the CRC to protect against masquerading. Enter a Data ID and counter to see how
            each mode treats it.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="space-y-2">
            <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
              Data ID (16-bit hex)
            </Label>
            <Input
              value={dataIdInput}
              onChange={(e) => setDataIdInput(e.target.value)}
              className="font-mono"
              placeholder="e.g. 0123"
              data-testid="dataid-input"
            />
          </div>
          <div className="space-y-2">
            <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
              Counter (0-14)
            </Label>
            <Input
              type="number"
              min={0}
              max={14}
              value={counter}
              onChange={(e) => setCounter(Number(e.target.value))}
              className="font-mono"
              data-testid="dataid-counter-input"
            />
          </div>

          {error && (
            <div className="flex items-center gap-2 text-sm text-destructive" data-testid="dataid-error">
              <AlertCircle className="h-4 w-4" /> {error}
            </div>
          )}

          <Button onClick={calculate} className="btn-premium w-full" data-testid="dataid-calculate-btn">
            Calculate
          </Button>
        </CardContent>
      </Card>

      <div className="lg:col-span-3 space-y-4" data-testid="dataid-result">
        {!result ? (
          <Card className="technical-card">
            <CardContent className="pt-6">
              <p className="text-sm text-muted-foreground">Enter a Data ID and click Calculate to see all four inclusion modes.</p>
            </CardContent>
          </Card>
        ) : (
          MODE_INFO.map((mode) => {
            const b = result.breakdown[mode.id];
            return (
              <Card key={mode.id} className="technical-card">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-4 mb-2">
                    <h3 className="font-chivo font-bold uppercase tracking-tight text-sm">{mode.name}</h3>
                    <Badge variant="outline" className="font-mono text-[10px] shrink-0">{mode.idSpace}</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground mb-3">{mode.description}</p>
                  <div className="flex flex-wrap gap-x-6 gap-y-1 font-mono text-xs pt-3 border-t border-structure">
                    <span>
                      <span className="text-muted-foreground">Bytes fed into CRC: </span>
                      <span className="text-autoxium-blue font-bold">{b.crcBytesHex}</span>
                    </span>
                    {b.explicit && (
                      <span>
                        <span className="text-muted-foreground">{b.explicit.label}: </span>
                        <span className="text-autoxium-blue font-bold">{b.explicit.valueHex}</span>
                      </span>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
};

export default DataIdCalculator;
